/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import { generateEventIdObject } from '@tracking/mbp-next/dist/dataLayer';
import useBrowserUUID from '../../../useBrowserUUID';

let gtmTrack = null;

const getGtmTrack = () => {
    if (!gtmTrack) {
        // eslint-disable-next-line global-require, import/no-extraneous-dependencies
        const dataLayerHelper = require('@tracking/mbp-next/dist/dataLayer');
        gtmTrack = new dataLayerHelper.DataLayerClient();
    }

    return gtmTrack;
};

const getCookieClientSide = (name) => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return '';

    if (window?.truste?.util?.readCookie) {
        return window?.truste?.util?.readCookie?.(name);
    }
    const pattern = new RegExp(`(?:^|; )${name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&')}=([^;]*)`);
    const match = document.cookie.match(pattern);
    return match ? decodeURIComponent(match[1]) : '';
};

const getPersistedMemberEmail = () => {
    if (typeof window === 'undefined' || !window.sessionStorage) {
        return '';
    }

    try {
        const persistedMember = window.sessionStorage.getItem('persist:member');
        if (!persistedMember) {
            return '';
        }

        const memberData = JSON.parse(persistedMember);
        if (!memberData?.profile) {
            return '';
        }

        const userProfile = JSON.parse(memberData.profile);
        return userProfile?.email || '';
    } catch (error) {
        return '';
    }
};

export const normalizeGtmUserEmail = (email = '') => {
    if (typeof email !== 'string') {
        return '';
    }

    const normalizedEmail = email.trim().toLowerCase();
    const [localPart, domainPart] = normalizedEmail.split('@');

    if (!localPart || !domainPart) {
        return normalizedEmail;
    }

    const normalizedLocalPart = localPart.split('+')[0].replace(/\./g, '');
    return `${normalizedLocalPart}@${domainPart}`;
};

const getGtmUserId = (dataLayer = {}) => normalizeGtmUserEmail(
    getPersistedMemberEmail() || dataLayer.customer_email || dataLayer.user_id,
);

const getOrigin = () => {
    if (typeof window !== 'undefined' && window.location && window.location.origin) {
        return window.location.origin;
    }

    return '';
};

const toAbsoluteUrl = (url = '') => {
    if (!url) {
        return '';
    }

    if (url.includes('http:') || url.includes('https:')) {
        return url;
    }

    return `${getOrigin()}${url}`;
};

const normalizeGtmEventName = (event = '') => event || 'page_view';

export const trackGtmEvent = (dataLayer = {}) => {
    const userId = getGtmUserId(dataLayer);
    const eventName = dataLayer?.ga_event_name || dataLayer.event_name || dataLayer.eventName;

    if (!eventName) {
        return false;
    }

    const eventNames = Array.isArray(eventName) ? eventName : [eventName];

    eventNames.forEach((event, index) => {
        if (!event) {
            return;
        }
        const eventPayload = {
            ...dataLayer,
            user_id: userId,
        };
        getGtmTrack().push({ data: eventPayload, event }, 'four_dot_two');
    });

    return eventNames.length > 0;
};

export const trackGtmPageView = (dl = {}, pageUrl = '', event = '') => {
    const fallbackPageUrl = typeof window !== 'undefined' ? window?.location?.href : '';
    const resolvedPageUrl = toAbsoluteUrl(pageUrl || dl.page_location || dl.page || fallbackPageUrl);
    const userId = getGtmUserId(dl);
    const browserUuid = dl.browser_uuid || useBrowserUUID();
    const eventName = normalizeGtmEventName(event);
    const { id } = generateEventIdObject();
    window.ev_raw_id = id;
    const pageViewData = {
        ...dl,
        trustarc_advertising: getCookieClientSide('notice_preferences') || getCookieClientSide('notice_behavior'),
        page: resolvedPageUrl,
        page_location: resolvedPageUrl,
        gua_title: dl.gua_title || dl.title || dl.page_name || dl.page_type,
        page_type: dl.page_type || dl.pageType,
        page_referrer: dl.page_referrer || dl.referrer,
        browser_uuid: browserUuid,
        user_id: userId,
        ev_raw_id: window.ev_raw_id,
    };

    getGtmTrack().push({
        data: pageViewData,
        event: eventName,
    }, 'four_dot_two');

    if (typeof window !== 'undefined') {
        window.gtmPvFired = true;
    }

    return true;
};
